import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Shield, Zap, Globe2, FileText, Wallet as WalletIcon, Package,
  CheckCircle2, ArrowRight, Lock, Activity, Terminal, Menu, X,
  ShieldCheck, Eye, IdCard, GitCompare, Fingerprint, BadgeCheck,
  Building2, ShoppingCart, CreditCard, ChevronDown, Camera, Megaphone,
  Banknote, Truck, Headphones, Sparkles, Send, Layers,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const PLATFORMS = [
  { name: "SelleSend", href: "https://sellesend.com/login", desc: "Retail commerce" },
  { name: "SelleCheck", href: "https://sellecheck.com/", desc: "Chargeback guarantee" },
  { name: "SelleCheck Gateway", href: "https://sellecheck.transactiongateway.com/", desc: "Payment gateway" },
  { name: "SellePages", href: "https://sellepages.com/", desc: "Endpoint marketing" },
  { name: "SelleTrust", href: "https://selletrust.com/", desc: "Trust & verification" },
  { name: "SelleWell", href: "https://sellewell.com/", desc: "Merchant advisory" },
  { name: "Companeze", href: "https://companeze.com/", desc: "Compliance & governance" },
  { name: "SmartSend", href: "https://sellesafe.smartlgstx.com/", desc: "Logistics rails" },
];

const carriers = ["UPS", "FEDEX", "DHL", "USPS", "TNT", "GLOBEGISTICS"];

const connectedSystems = [
  "SHOPIFY", "WOOCOMMERCE", "BIGCOMMERCE", "MAGENTO", "WIX", "SQUARESPACE",
  "STRIPE", "NMI", "AUTHORIZE.NET", "PAYPAL", "SQUARE", "ADYEN",
  "BRAINTREE", "WORLDPAY", "CHASE PAYMENTECH", "FIRST DATA", "ELAVON", "TSYS",
];

// Enterprise merchants + card networks that run on Adyen / Stripe / NMI rails —
// the same processing infrastructure Sellepack plugs into.
const enterpriseNetworks = [
  "UBER", "MCDONALD'S", "EBAY", "MICROSOFT", "SPOTIFY", "NETFLIX",
  "AIRBNB", "ETSY", "HBO MAX", "DYSON", "7-ELEVEN", "L'OREAL",
  "DOMINO'S", "DELIVEROO", "CASPER", "PATAGONIA", "LYFT", "CANVA",
  "VISA", "MASTERCARD", "AMEX", "DISCOVER", "UNIONPAY", "JCB",
  "APPLE PAY", "GOOGLE PAY", "SAMSUNG PAY", "PAYPAL", "VENMO", "CASH APP",
  "KLARNA", "AFTERPAY", "AFFIRM", "ALIPAY", "WECHAT PAY", "SEPA",
  "IDEAL", "BANCONTACT", "GIROPAY", "BLIK", "SOFORT", "MULTIBANCO",
];

// Global & regional carriers that Sellepack's aggregator stack (EasyPost,
// Webship, Easyship, direct connections) routes through.
const carrierNetwork = [
  "UPS", "FEDEX", "DHL", "USPS", "USPS PRIORITY MAIL", "USPS GROUND ADVANTAGE",
  "FEDEX GROUND", "FEDEX EXPRESS", "UPS GROUND", "UPS WORLDWIDE", "DHL EXPRESS", "DHL ECOMMERCE",
  "ARAMEX", "TNT", "PUROLATOR", "CANADA POST", "ROYAL MAIL", "DPD",
  "GLS", "HERMES", "CHRONOPOST", "LA POSTE", "POSTE ITALIANE", "CORREOS",
  "ONTRAC", "LASERSHIP", "PITNEY BOWES", "ASENDIA", "SENDLE", "AUSTRALIA POST",
  "JAPAN POST", "SINGPOST", "EMS", "SF EXPRESS", "YAMATO", "SAGAWA",
  "DB SCHENKER", "KUEHNE+NAGEL", "YANWEN", "CAINIAO", "YUN EXPRESS", "PARCELFORCE",
];

const smartServices = [
  { code: "SMART_SELLER", name: "Smart Seller", tag: "Digital safety check on payments", price: 5.00, icon: ShieldCheck, color: "#0EA5E9" },
  { code: "SMART_TRACK", name: "Smart Track", tag: "Human observation & reconciliation during shipment", price: 5.00, icon: Eye, color: "#6366F1" },
  { code: "SMART_START", name: "Smart Start", tag: "Pre-ship fraud check on every data point", price: 5.00, icon: Zap, color: "#F59E0B" },
  { code: "SMART_ID", name: "Smart ID", tag: "Buyer identity verification", price: 5.00, icon: IdCard, color: "#8B5CF6" },
  { code: "SMART_SHIP", name: "Smart Ship", tag: "Buy/sell comparison analysis with identity", price: 5.00, icon: GitCompare, color: "#D946EF" },
  { code: "SMART_VERIFY", name: "Smart Verify", tag: "Digital fingerprinting of the item", price: 5.00, icon: Fingerprint, color: "#10B981" },
  { code: "SELLE_VERIFY", name: "SelleVerify", tag: "Pre-ship fraud screen — IP, email & geo risk", price: 5.00, icon: Shield, color: "#0052FF" },
  { code: "SELLE_SEND", name: "SelleSend", tag: "Branded buyer notifications — SMS / WhatsApp / Email", price: 5.00, icon: Send, color: "#2EB358" },
  { code: "ALL_TOOLS", name: "All Secondary Tools — Bundle", tag: "Every secondary fraud-mitigation tool on one ticket", price: 7.99, bundle: true, icon: Layers, color: "#0F2A5C" },
  { code: "SELLE_CHECK", name: "Selle Check", tag: "Chargeback guarantee & instant payout", price: 0, flagship: true, tieredPercent: "1.00–1.25%", icon: BadgeCheck, color: "#0052FF" },
];

export default function Landing() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white">
      {/* Wholesale identity banner */}
      <div data-testid="wholesale-top-banner" className="brand-bg text-white text-[11px] sm:text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-1.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-mono-plex tracking-wider uppercase">
            <ShieldCheck size={12} />
            <span>SellePack Wholesale · For organizations · Distributed exclusively through approved partners</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-white/80">
            <a href="https://sellesend.com" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="banner-retail-link">
              Retail? Visit SelleSend →
            </a>
          </div>
        </div>
      </div>
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <span
              data-testid="nav-wholesale-badge"
              className="hidden sm:inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.2em] uppercase brand-bg text-white px-2 py-0.5 rounded-sm"
            >
              <ShieldCheck size={10} /> Wholesale
            </span>
          </div>
          <div className="hidden md:flex items-center gap-7 text-sm text-zinc-600">
            <div className="relative" onMouseLeave={() => setPlatformsOpen(false)}>
              <button
                data-testid="nav-platforms-toggle"
                onMouseEnter={() => setPlatformsOpen(true)}
                onClick={() => setPlatformsOpen((o) => !o)}
                className="flex items-center gap-1 hover:text-zinc-900"
              >
                Platforms <ChevronDown size={14} className={`transition-transform ${platformsOpen ? "rotate-180" : ""}`} />
              </button>
              {platformsOpen && (
                <div
                  data-testid="nav-platforms-menu"
                  className="absolute left-0 top-full pt-2 w-72 z-50"
                >
                  <div className="border border-zinc-200 rounded-md bg-white shadow-xl p-2">
                    <div className="px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 border-b border-zinc-100">
                      Sellepack Network
                    </div>
                    {PLATFORMS.map((p) => (
                      <a
                        key={p.name}
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`nav-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="block px-3 py-2 text-sm rounded-sm hover:bg-zinc-50"
                      >
                        <div className="font-semibold text-zinc-900">{p.name}</div>
                        <div className="text-[11px] text-zinc-500">{p.desc}</div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="#how-it-works" className="hover:text-zinc-900">How it works</a>
            <a href="#services" className="hover:text-zinc-900">Smart Services</a>
            <a href="#payments" className="hover:text-zinc-900">Payments</a>
            <a href="#pricing" className="hover:text-zinc-900">Pricing</a>
            <a href="#security" className="hover:text-zinc-900">Security</a>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-zinc-700 hover:text-zinc-900 px-3 py-2">Login</Link>
            <Link to="/register" data-testid="nav-register-link" className="text-sm brand-bg text-white px-4 sm:px-5 py-2 rounded-sm hover:opacity-90">Open account</Link>
          </div>
          <button data-testid="landing-mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className="sm:hidden text-zinc-700 p-1">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {mobileOpen && (
          <div className="sm:hidden border-t border-zinc-200 bg-white px-4 py-4 space-y-3" data-testid="landing-mobile-menu">
            <a href="#how-it-works" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">How it works</a>
            <a href="#services" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">Smart Services</a>
            <a href="#payments" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">Payments</a>
            <a href="#carriers" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">Carriers</a>
            <a href="#pricing" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">Pricing</a>
            <a href="#security" onClick={() => setMobileOpen(false)} className="block text-sm text-zinc-700 py-1">Security</a>
            <div className="flex gap-2 pt-2 border-t border-zinc-200">
              <Link to="/login" className="flex-1 text-center text-sm border border-zinc-300 py-2 rounded-sm">Login</Link>
              <Link to="/register" className="flex-1 text-center text-sm brand-bg text-white py-2 rounded-sm">Open account</Link>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24 md:py-32 relative">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1 border border-zinc-300 rounded-sm text-[10px] sm:text-xs tracking-[0.2em] uppercase font-medium text-zinc-700 mb-6 sm:mb-8">
                <span className="w-1.5 h-1.5 rounded-full brand-bg" /> SellePack Wholesale · Global Solutions / Protected
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="font-display text-4xl sm:text-5xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-zinc-900">
                Global solutions.<br /><span className="brand-text">Protected.</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
                className="mt-6 sm:mt-8 text-sm sm:text-base md:text-lg text-zinc-600 max-w-xl leading-relaxed">
                SellePack is the <b>wholesale</b> fulfillment infrastructure of the Sellepack network, built for
                organizations — distribution partners, resellers, enterprise programs, and marketplace operators.
                Each platform in the network operates with <b>independent production capabilities</b>. Access is
                provisioned <b>exclusively through our chosen partners</b>. Each partner program runs on its own
                customized program and customized pricing structure, tailored to the partner's organization needs.
              </motion.p>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
                className="mt-8 sm:mt-10 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-primary-cta" className="brand-bg text-white px-5 sm:px-6 py-3 rounded-sm font-medium hover:opacity-90 flex items-center gap-2 text-sm sm:text-base">
                  Request partner access <ArrowRight size={16} />
                </Link>
                <Link to="/login" className="border border-zinc-300 text-zinc-900 px-5 sm:px-6 py-3 rounded-sm font-medium hover:bg-zinc-50 text-sm sm:text-base">
                  Partner sign-in
                </Link>
              </motion.div>
              <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-4 sm:gap-6 text-[10px] sm:text-xs text-zinc-500 tracking-wide uppercase">
                <span className="flex items-center gap-1"><Lock size={12} /> SOC2-ready</span>
                <span className="flex items-center gap-1"><Shield size={12} /> Wallet-funded</span>
                <span className="flex items-center gap-1"><Activity size={12} /> Chargeback Guarantee</span>
              </div>
            </div>
            <div className="md:col-span-5 relative">
              <div className="aspect-square max-w-md mx-auto relative border border-zinc-200 rounded-md bg-white overflow-hidden noise">
                <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
                  <defs>
                    <radialGradient id="glow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="var(--brand-accent)" stopOpacity="0.18" />
                      <stop offset="100%" stopColor="var(--brand-accent)" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  <circle cx="200" cy="200" r="140" fill="url(#glow)" />
                  {[40, 80, 120, 160].map((r) => <circle key={r} cx="200" cy="200" r={r} fill="none" stroke="#E4E4E7" strokeWidth="1" />)}
                  <path className="arc" d="M 60 200 Q 200 60 340 200" stroke="var(--brand-accent)" strokeWidth="2" fill="none" />
                  <path className="arc" d="M 60 220 Q 200 340 340 220" stroke="var(--brand-accent)" strokeWidth="2" fill="none" />
                  {[[80, 180, "NYC"], [320, 180, "LA"], [200, 80, "LDN"], [200, 320, "TOK"]].map(([x, y, l]) => (
                    <g key={l}>
                      <circle cx={x} cy={y} r="5" fill="var(--brand-accent)" />
                      <text x={x + 10} y={y + 4} fontSize="10" fontFamily="IBM Plex Mono" fill="#09090B">{l}</text>
                    </g>
                  ))}
                  <rect x="180" y="180" width="40" height="40" fill="var(--brand-accent)" rx="2" />
                  <text x="200" y="206" fontSize="10" fontFamily="IBM Plex Mono" fill="#fff" textAnchor="middle">SP</text>
                </svg>
                <div className="absolute bottom-3 left-3 right-3 font-mono-plex text-[9px] sm:text-[10px] text-zinc-700 flex justify-between">
                  <span>ROUTING ENGINE v4.1</span>
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> LIVE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carriers marquee */}
      <section className="border-y border-zinc-200 py-5 sm:py-6 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center gap-6 sm:gap-8">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">Connected carriers</span>
          <div className="flex gap-6 sm:gap-12 font-display font-black text-sm sm:text-xl text-zinc-300 tracking-tight overflow-hidden">
            {carriers.concat(carriers).map((c, i) => <span key={i} className="whitespace-nowrap">{c}</span>)}
          </div>
        </div>
      </section>

      {/* Our Principles */}
      <section id="principles" className="bg-white border-b border-zinc-200 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl mb-10 sm:mb-14">
            <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Our Principles</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
              The wholesale merchant<br /><span className="brand-text">is always right — no exceptions.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-zinc-600 max-w-2xl">
              Human interaction and support, no matter the cost. Same-day responses on every wholesale account — typical SLA.
              You’re the client, we contact you with the products you need.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Headphones, t: "24/7/365 Live Call Center", b: "A real person, instantly. No AI-first triage. Domestic operators help reconcile shipments, verify buyers, and resolve carrier disputes." },
              { icon: Sparkles, t: "Connectivity is our core product", b: "eComm and business infrastructure solutions across 175 banks, 125 carts, 200 carriers — one wholesale relationship." },
              { icon: ShieldCheck, t: "AML compliance, included", b: "Premium wholesale accounts receive an AML compliance program — we monitor every transaction and shipment for AML optimization." },
              { icon: Fingerprint, t: "SelleVerify forensic check", b: "Analyzes product counterfeit patterns and verifies items shipped match origin. Blockchain registration with a service mark for authenticity." },
              { icon: Megaphone, t: "Endpoint exposure", b: "Expose your products through our endpoint management platform — get in front of billions of customers via the SellePages network." },
              { icon: Building2, t: "Governance & advisory", b: "Companeze (related company) provides corporate governance, compliance, and business-advisory reporting on every premium account." },
            ].map(({ icon: Icon, t, b }) => (
              <div key={t} className="border border-zinc-200 rounded-md p-5 sm:p-6 bg-white hover:border-zinc-400 transition-colors">
                <Icon size={20} strokeWidth={1.7} className="brand-text" />
                <div className="font-display font-bold text-zinc-900 mt-3">{t}</div>
                <div className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Click Pic — 4 Programs, One Click */}
      <section id="click-pic" className="bg-zinc-50 border-b border-zinc-200 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl mb-10 sm:mb-14">
            <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">From your phone</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
              Click Pic. <span className="brand-text">4 Programs. One Click.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-zinc-600 max-w-2xl">
              Snap a photo of any item. Click Pic activates four integrated programs — each runs independently or as a complete end-to-end process.
              Marketing, fraud reduction, payment connectivity, shipping infrastructure — one wholesale workflow.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { n: "01", icon: Megaphone, t: "Marketing", b: "Expose your product through our endpoint management platform. Get in front of billions of customers. SelleVerify registers and supports authenticity verification.", bullets: ["Product listing & syndication", "Blockchain product fingerprint", "Authenticity verification"] },
              { n: "02", icon: Shield, t: "Fraud Protection", b: "Every transaction screened for fraud, scams, and AML compliance. Forensic examination verifies the product is the same item shipped.", bullets: ["Real-time fraud screening", "AML compliance monitoring", "Counterfeit pattern analysis"] },
              { n: "03", icon: Banknote, t: "Payments", b: "Secure processing through Stripe, NMI / Companeze, or your existing merchant account — and free in-network eChecks that clear in seconds.", bullets: ["Stripe LIVE + NMI / Companeze", "Free in-network eChecks", "Optional Global Settlement"] },
              { n: "04", icon: Truck, t: "Shipping + Guarantees", b: "AI-powered dimensions, multi-gateway rate comparison, instant label generation. Shipment protection options and operational coverage programs.", bullets: ["AI dimension estimation", "5 gateways · 200 carriers", "Shipment protection options"] },
            ].map(({ n, icon: Icon, t, b, bullets }) => (
              <div key={n} data-testid={`click-pic-program-${n}`} className="border border-zinc-200 rounded-md p-5 sm:p-6 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold tracking-[0.2em] uppercase brand-text">Program {n}</span>
                  <Icon size={18} className="text-zinc-400" />
                </div>
                <div className="font-display font-black text-xl sm:text-2xl mt-3 text-zinc-900">{t}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{b}</p>
                <ul className="mt-4 space-y-1.5 text-xs text-zinc-700">
                  {bullets.map((x) => <li key={x} className="flex items-start gap-2"><CheckCircle2 size={12} className="brand-text mt-0.5 shrink-0" />{x}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-2 text-[10px] sm:text-xs font-mono-plex tracking-wider text-zinc-500 uppercase">
            <Camera size={14} className="brand-text" />
            <span>The Complete Process</span>
            <span>—</span>
            {["Snap", "Market", "Verify", "Pay", "Ship", "Guarantee"].map((s) => (
              <span key={s} className="border border-zinc-300 px-2 py-0.5 rounded-sm">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Scale of Our Network */}
      <section id="scale" className="bg-white border-b border-zinc-200 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl mb-10 sm:mb-14">
            <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Connect and partner with</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
              The scale of <span className="brand-text">our network.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              ["200", "Shipping Companies"],
              ["175", "Banks"],
              ["125", "Shopping Carts"],
              ["$500B", "In Payments"],
            ].map(([n, l]) => (
              <div key={l} data-testid={`scale-stat-${l.toLowerCase().replace(/\s+/g, "-")}`} className="border border-zinc-200 rounded-md p-5 sm:p-7 bg-white">
                <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-900">{n}</div>
                <div className="mt-2 text-xs sm:text-sm text-zinc-600">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              ["Fraud, Risk & AML", "Comprehensive fraud prevention, risk management, and anti-money-laundering compliance — every wholesale shipment."],
              ["eComm Infrastructure", "Complete business infrastructure solutions for online and in-person commerce, plus negotiated wholesale rates."],
              ["Nationwide Logistics", "Drop-off, pickup, and delivery solutions through our logistics partner network — same-day options included."],
            ].map(([t, b]) => (
              <div key={t} className="border border-zinc-200 rounded-md p-5 bg-white">
                <div className="font-display font-bold text-zinc-900">{t}</div>
                <div className="text-sm text-zinc-600 mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Easy Steps */}
      <section id="how-it-works" className="bg-white border-b border-zinc-200 py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-2xl mb-10 sm:mb-16">
            <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">How it works</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
              Three easy steps<br /><span className="brand-text">to success.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                n: "01",
                title: "Apply for an account",
                body: "Tell us who you are. We review every application to protect the network and our guarantees.",
                kicker: "Sign up",
              },
              {
                n: "02",
                title: "Get approved — we wire you up",
                body: "Once approved, load your carrier account (UPS, FedEx, DHL, USPS) or use ours. Your payments wallet is provisioned automatically.",
                kicker: "Approved",
              },
              {
                n: "03",
                title: "Simply sell and send",
                body: "Book shipments in one click. Layer on Smart Services for fraud, identity, and chargeback protection. Ship smarter, every time.",
                kicker: "Ship",
              },
            ].map((s, i) => (
              <div key={s.n} className="relative bg-white border border-zinc-200 rounded-md p-6 sm:p-8 hover:border-zinc-400 transition-colors">
                <div className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-zinc-200 leading-none">{s.n}</div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] brand-text font-bold">{s.kicker}</div>
                <h3 className="font-display text-lg sm:text-xl font-bold mt-1 text-zinc-900">{s.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{s.body}</p>
                {i < 2 && <ArrowRight size={16} className="hidden md:block absolute -right-3 top-1/2 text-zinc-300 bg-white" />}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/register" data-testid="steps-cta" className="inline-flex brand-bg text-white px-6 sm:px-8 py-3 rounded-sm font-medium hover:opacity-90 text-sm sm:text-base items-center gap-2">
              Apply for your account <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Smart Services */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24 md:py-32">
        <div className="max-w-3xl mb-10 sm:mb-16">
          <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">01 — Smart Services</div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
            Every secondary fraud-mitigation tool —<br />
            <span className="brand-text">flat $5.00 each. $7.99 for the full bundle.</span>
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-zinc-600 max-w-2xl">
            Eight secondary fraud-mitigation tools, all flat-priced at <b>$5.00 per shipment</b>. Activate any
            combination on the booking — or take <b>every tool on one ticket for $7.99</b>. Selle Check is the
            only percentage-based service: it pays you for items sold and <b>guarantees against chargebacks</b>.
          </p>
          <p className="mt-3 text-xs sm:text-sm text-zinc-500 max-w-2xl">
            Final per-shipment pricing for each partner organization is set in the partner program (see Wholesale Access below).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {smartServices.map((s) => {
            const Icon = s.icon;
            const accent = s.bundle ? "border-zinc-900 bg-zinc-900 text-white" : (s.flagship ? "border-[var(--brand-accent)] shadow-lg sm:col-span-2 lg:col-span-3" : "border-zinc-200");
            return (
              <div key={s.code} className={`relative border rounded-md p-5 sm:p-6 ${s.bundle ? "" : "bg-white"} ${accent}`} data-testid={`landing-service-${s.code}`}>
                {s.flagship && <div className="absolute -top-2 left-5 text-[10px] font-bold tracking-[0.2em] uppercase brand-bg text-white px-2 py-0.5 rounded-sm">Flagship</div>}
                {s.bundle && <div className="absolute -top-2 left-5 text-[10px] font-bold tracking-[0.2em] uppercase bg-[var(--brand-accent)] text-white px-2 py-0.5 rounded-sm">Bundle</div>}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: s.bundle ? "rgba(255,255,255,0.12)" : `${s.color}18`, color: s.bundle ? "#fff" : s.color }}>
                    <Icon size={20} strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2 justify-between">
                      <div className={`font-display text-lg sm:text-xl font-bold ${s.bundle ? "text-white" : "text-zinc-900"}`}>{s.name}</div>
                      <div className="font-mono-plex text-sm font-semibold" style={{ color: s.bundle ? "#fff" : s.color }}>
                        {s.price > 0 ? <>${s.price.toFixed(2)}</> : null}
                        {s.tieredPercent && <span className={`${s.price > 0 ? "text-xs text-zinc-500 font-normal" : ""}`}>{s.price > 0 ? " + " : ""}{s.tieredPercent}</span>}
                      </div>
                    </div>
                    <div className={`text-sm mt-1 ${s.bundle ? "text-zinc-300" : "text-zinc-600"}`}>{s.tag}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Payments & Guarantees */}
      <section id="payments" className="bg-zinc-900 text-white py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-5">
              <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-3 sm:mb-4">02 — Payments &amp; Guarantees</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter">
                Connect your<br />merchant account.<br /><span className="brand-text">Or use ours.</span>
              </h2>
              <p className="mt-5 sm:mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed">
                Plug in your existing processor or settle straight onto SellePack rails — either way, our <b>Selle Check</b> guarantees attach automatically.
                Our Selle Check solutions connect to <b>over 175 processing banks</b>, <b>125 shopping carts</b>, <b>Shopify</b> and other systems to keep your e-comm easy.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/register" data-testid="payments-cta-apply" className="brand-bg text-white px-5 sm:px-6 py-3 rounded-sm font-medium hover:opacity-90 inline-flex items-center gap-2 text-sm sm:text-base">
                  Apply for an account <ArrowRight size={16} />
                </Link>
                <a href="#services" data-testid="payments-cta-learn" className="border border-zinc-600 text-white px-5 sm:px-6 py-3 rounded-sm font-medium hover:bg-zinc-800 inline-flex items-center gap-2 text-sm sm:text-base">
                  See guarantees
                </a>
              </div>
            </div>

            <div className="md:col-span-7 grid grid-cols-2 gap-3 sm:gap-4">
              <div data-testid="payments-stat-banks" className="border border-zinc-700 rounded-md p-5 sm:p-6 bg-zinc-800/40 backdrop-blur">
                <Building2 size={22} className="brand-text" />
                <div className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tighter">175+</div>
                <div className="mt-1 text-xs sm:text-sm text-zinc-400">Processing banks connected</div>
              </div>
              <div data-testid="payments-stat-carts" className="border border-zinc-700 rounded-md p-5 sm:p-6 bg-zinc-800/40 backdrop-blur">
                <ShoppingCart size={22} className="brand-text" />
                <div className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tighter">125+</div>
                <div className="mt-1 text-xs sm:text-sm text-zinc-400">Shopping carts supported</div>
              </div>
              <div data-testid="payments-stat-shopify" className="border border-zinc-700 rounded-md p-5 sm:p-6 bg-zinc-800/40 backdrop-blur">
                <CreditCard size={22} className="brand-text" />
                <div className="mt-4 font-display font-black text-2xl sm:text-3xl tracking-tighter">Shopify</div>
                <div className="mt-1 text-xs sm:text-sm text-zinc-400">+ WooCommerce, BigCommerce, Magento, Wix</div>
              </div>
              <div data-testid="payments-stat-guarantee" className="border border-[var(--brand-accent)] rounded-md p-5 sm:p-6 bg-zinc-800/40 backdrop-blur">
                <BadgeCheck size={22} className="brand-text" />
                <div className="mt-4 font-display font-black text-2xl sm:text-3xl tracking-tighter">Auto-attach</div>
                <div className="mt-1 text-xs sm:text-sm text-zinc-400">Selle Check guarantees on every approved order</div>
              </div>
            </div>
          </div>

          <div className="mt-10 sm:mt-14 grid sm:grid-cols-3 gap-4 sm:gap-6 text-sm">
            {[
              ["Bring your own merchant", "Use Stripe, NMI, or your existing processor — we route the funds and you keep the relationship."],
              ["Or settle on SellePack rails", "We handle KYC, tokenized vault, and instant payout from approved Selle Check orders."],
              ["Either way, the guarantee is the same", "Chargeback protection, fraud blocking, and human review fire automatically before and after the shipment."],
            ].map(([t, b]) => (
              <div key={t} className="border border-zinc-700 rounded-md p-4 sm:p-5 bg-zinc-800/30">
                <div className="font-display font-bold">{t}</div>
                <div className="text-zinc-400 mt-1">{b}</div>
              </div>
            ))}
          </div>

          {/* Connected systems marquee */}
          <div data-testid="payments-connected-systems" className="mt-12 sm:mt-16 border-t border-zinc-800 pt-8 sm:pt-10">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4 sm:mb-5">Connected systems</div>
            <div className="overflow-hidden relative" style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
              <div className="ticker flex gap-8 sm:gap-14 font-display font-black text-base sm:text-2xl text-zinc-600 tracking-tight whitespace-nowrap" style={{ width: "max-content" }}>
                {connectedSystems.concat(connectedSystems).map((s, i) => (
                  <span key={i} className="hover:text-white transition-colors">{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Enterprise networks marquee — big brands + card networks on the same rails */}
          <div data-testid="payments-enterprise-networks" className="mt-6 sm:mt-8">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4 sm:mb-5">Enterprise rails &amp; networks we plug into</div>
            <div className="overflow-hidden relative" style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
              <div className="ticker-reverse flex gap-8 sm:gap-14 font-display font-black text-base sm:text-2xl text-zinc-500 tracking-tight whitespace-nowrap" style={{ width: "max-content" }}>
                {enterpriseNetworks.concat(enterpriseNetworks).map((s, i) => (
                  <span key={i} className="hover:text-white transition-colors">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carrier network */}
      <section id="carriers" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24">
        <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">Carrier network</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 sm:mb-10">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900 max-w-3xl">
            One contract. <span className="brand-text">Every carrier.</span>
          </h2>
          <p className="text-sm sm:text-base text-zinc-600 max-w-md">
            SellePack aggregates across EasyPost, Easyship, Webship, and direct carrier accounts — rate-shopping, booking, labels, and tracking through one API.
          </p>
        </div>
        <div data-testid="carrier-network-marquee" className="border-y border-zinc-200 py-8 sm:py-10">
          <div className="overflow-hidden relative" style={{ maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" }}>
            <div className="ticker flex gap-10 sm:gap-16 font-display font-black text-lg sm:text-3xl text-zinc-300 tracking-tight whitespace-nowrap" style={{ width: "max-content" }}>
              {carrierNetwork.concat(carrierNetwork).map((s, i) => (
                <span key={i} className="hover:text-zinc-900 transition-colors">{s}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 text-xs text-zinc-500">
          End users never see carrier names on rate cards — only &ldquo;Ship 1 / Ship 2 / Ship 3 / Ship 4&rdquo; labels — so your vendor relationships stay private.
        </div>
      </section>

      {/* Platform features */}
      <section id="features" className="bg-zinc-50 border-y border-zinc-200 py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="max-w-2xl mb-10 sm:mb-16">
            <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">03 — Platform</div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
              Infrastructure built for<br />high-stakes shipping.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
            <Feature className="md:col-span-8" icon={Package} title="Multi-carrier rate shopping"
              body="Instant quotes across UPS, FedEx, DHL, USPS, TNT & more — ranked by price, transit, and reliability. Book with one click, label and commercial invoice included." />
            <Feature className="md:col-span-4" icon={WalletIcon} title="Wallet-first billing"
              body="Prepay once. Auto-debit on booking, auto-refund on void, auto-rebill on surcharge." />
            <Feature className="md:col-span-4" icon={Shield} title="Surcharge rebilling"
              body="Every post-shipment adjustment is captured, itemized, and settled against the prepaid wallet." />
            <Feature className="md:col-span-4" icon={FileText} title="Immutable file vault"
              body="Every label, commercial invoice, and manifest stored with audit trail and one-click retrieval." />
            <Feature className="md:col-span-4" icon={Globe2} title="Carrier connection program"
              body="Connect your negotiated carrier accounts and layer custom coverages on top — by approval." />
            <Feature className="md:col-span-6" icon={Terminal} title="Full REST API"
              body="Every feature — quote, book, track, void, label, manifest, smart services — is available via API." />
            <Feature className="md:col-span-6" icon={Zap} title="Real-time tracking"
              body="Unified tracking events across carriers with webhooks, dashboards, and customer-facing share links." />
          </div>
        </div>
      </section>

      {/* Pricing — Wholesale Access Tiers */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24 md:py-32">
        <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">04 — Wholesale Access</div>
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900 mb-4">
          Customized programs.<br /><span className="brand-text">Customized pricing structures.</span>
        </h2>
        <p className="text-sm sm:text-base text-zinc-600 max-w-3xl mb-6 sm:mb-8">
          SellePack is a wholesale platform for organizations. <b>Each partner setup runs on a customized program
          and a customized pricing structure</b> — built around the partner's volume, vertical, risk profile, and
          operating model. Per-shipment surcharges, smart-service activations, protection products, billing cadence,
          and merchant-account routing are all set inside the partner agreement.
        </p>
        <div className="border border-zinc-900 bg-zinc-900 text-white rounded-md p-5 sm:p-7 mb-10 sm:mb-14" data-testid="distribution-partner-disclaimer">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--brand-accent)] mb-2">Distribution Partner Disclosure</div>
          <p className="text-sm sm:text-base leading-relaxed">
            Each platform in the Sellepack network — <b>SelleSend</b>, <b>SelleCheck</b>, <b>SelleCheck Gateway</b>, <b>SellePack</b>,
            <b> SellePages</b>, <b>SelleTrust</b>, <b>SelleWell</b>, <b>Companeze</b>, and <b>SmartSend</b> — operates with
            <b> independent production capabilities</b> and an independent commercial relationship with its end users. This
            program and its negotiated wholesale rates, Smart Services, protection products, and merchant-account access
            are <b>provided exclusively through our chosen distribution partners</b>.
          </p>
          <p className="text-sm sm:text-base leading-relaxed mt-3">
            These offers <b>cannot be obtained individually</b>, are not available through retail channels, and are not
            sold à la carte outside the partner program. <b>Each partner setup has a customized program and a customized
            pricing structure</b> defined by the partner agreement. Eligibility, activation, and ongoing access are subject
            to partner approval and the program's underwriting standards.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {[
            {
              name: "Partner Program A",
              tag: "Volume-based wholesale",
              approval: true,
              bullets: [
                "Customized per-shipment surcharge",
                "All Smart Services available (flat / bundled)",
                "Wallet billing with partner-set cadence",
                "Partner-managed onboarding",
              ],
            },
            {
              name: "Partner Program B",
              tag: "Enterprise distribution",
              approval: true,
              highlight: true,
              bullets: [
                "Custom rate card + carrier overrides",
                "Special insurance & coverage tiers",
                "Dedicated underwriting & AML monitoring",
                "Co-branded buyer notifications optional",
              ],
            },
            {
              name: "Direct Connect",
              tag: "Negotiated by partner",
              approval: true,
              bullets: [
                "Partner-negotiated carrier accounts",
                "Custom coverages, rebates & SLA",
                "Volume Smart Services pricing",
                "Named partner account manager",
              ],
            },
          ].map((p) => (
            <div
              key={p.name}
              data-testid={`pricing-tier-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative bg-white border ${p.highlight ? "border-[var(--brand-accent)] shadow-lg" : "border-zinc-200"} rounded-md p-6 sm:p-8 flex flex-col`}
            >
              {p.approval && (
                <div className="absolute -top-2 right-4 text-[10px] font-bold tracking-[0.2em] uppercase bg-zinc-900 text-white px-2 py-0.5 rounded-sm">
                  By partner approval
                </div>
              )}
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-zinc-500">{p.tag}</div>
              <h3 className="font-display text-xl sm:text-2xl font-black mt-2">{p.name}</h3>
              <div className="mt-4 sm:mt-6 font-display font-black text-3xl sm:text-4xl tracking-tighter brand-text">
                Custom pricing
              </div>
              <div className="text-xs text-zinc-500 mt-1">Set by your distribution-partner agreement</div>
              <ul className="mt-5 sm:mt-6 space-y-2 text-sm text-zinc-600 flex-1">
                {p.bullets.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 size={14} className="brand-text mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <Link
                to="/register"
                data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`mt-6 sm:mt-8 block text-center py-2 rounded-sm font-medium text-sm sm:text-base ${p.highlight ? "brand-bg text-white" : "border border-zinc-300 text-zinc-900 hover:bg-zinc-50"}`}
              >
                Request partner access
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-8 text-xs text-zinc-500 max-w-3xl">
          The above are <i>illustrative</i> program structures. The actual program, billing cadence, surcharge schedule,
          Smart Service activations, protection products, and merchant-account routing for your organization are
          finalized in your signed partner agreement.
        </div>
      </section>

      {/* Security */}
      <section id="security" className="bg-zinc-50 border-y border-zinc-200 py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3 sm:mb-4">05 — Security</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
                High-value commerce<br />deserves high-trust infrastructure.
              </h2>
              <p className="mt-5 sm:mt-6 text-sm sm:text-base text-zinc-600">
                Every Smart Service leaves an immutable audit trail. Selle Check is backed by a guarantee fund that pays out instantly on qualifying chargebacks.
              </p>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {[
                ["Chargeback guarantee", "Selle Check pays you for items sold — dispute-proof by design."],
                ["Digital fingerprinting", "Smart Verify captures a dispute-proof fingerprint of every shipped item."],
                ["Human-monitored transit", "Smart Track attaches a live human reconciler to high-value shipments."],
                ["Wallet pre-funding", "Funds held in your platform account — debited only on successful booking. Not an escrow service."],
              ].map(([t, b]) => (
                <div key={t} className="border border-zinc-200 rounded-md p-4 sm:p-5 bg-white">
                  <div className="font-display font-bold text-zinc-900">{t}</div>
                  <div className="text-sm text-zinc-600 mt-1">{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Billing Terms */}
      <section id="billing-terms" className="bg-zinc-50 border-y border-zinc-200 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-5">
              <div className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Billing terms</div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-zinc-900">
                Simple. Transparent.<br /><span className="brand-text">Non-negotiable.</span>
              </h2>
              <p className="mt-5 text-sm sm:text-base text-zinc-700 leading-relaxed">
                Our programs are designed to give wholesale merchants optimal protection and convenience. Our billing policies are simple.
              </p>
              <p className="mt-3 text-sm sm:text-base text-zinc-700 leading-relaxed">
                We expect to be paid upon demand — through the SellePack wallet, by credit card, or by other certified funds.
                We give our all for you and provide protection when you need it. <b>We do not extend terms or unpaid bills.</b> If
                your invoices are unpaid, the service or protection is not valid.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { t: "SellePack Wallet", b: "Pre-funded balance · auto-debited on every booking", icon: WalletIcon },
                { t: "Credit Card", b: "Stripe LIVE or NMI / Companeze · instant payment", icon: CreditCard },
                { t: "Certified Funds", b: "Wire transfer or ACH · for Direct Connect wholesale accounts", icon: Banknote },
              ].map(({ t, b, icon: Icon }) => (
                <div key={t} className="border border-zinc-200 rounded-md p-5 bg-white">
                  <Icon size={20} className="brand-text" />
                  <div className="font-display font-bold text-zinc-900 mt-3">{t}</div>
                  <div className="text-xs text-zinc-600 mt-1.5 leading-relaxed">{b}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Regulatory + Shipping role disclosure */}
          <div className="mt-12 sm:mt-16 grid md:grid-cols-2 gap-4 sm:gap-6" data-testid="regulatory-disclosure-block">
            <div className="border border-zinc-200 rounded-md p-5 sm:p-6 bg-white">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase brand-text mb-2">Regulatory status</div>
              <div className="font-display font-bold text-zinc-900">Platform Role Disclosure</div>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Our platform provides software, payment connectivity, shipping, verification, and business infrastructure
                solutions for independent wholesale merchants. Merchants maintain direct relationships with payment processors,
                gateways, financial institutions, and service providers. <b>We do not act as a bank, money transmitter, payment
                facilitator, escrow company, or financial institution.</b> We do not aggregate, pool, hold, or transfer customer
                funds on behalf of merchants.
              </p>
              <Link to="/legal/regulatory-disclosure" data-testid="footer-regulatory-link" className="inline-flex items-center gap-1 mt-3 text-xs brand-text hover:underline">
                Read full regulatory disclosure <ArrowRight size={12} />
              </Link>
            </div>
            <div className="border border-zinc-200 rounded-md p-5 sm:p-6 bg-white">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase brand-text mb-2">Shipping role</div>
              <div className="font-display font-bold text-zinc-900">Platform Role in Shipping</div>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Merchants maintain direct relationships with participating carriers and logistics providers. The platform
                provides operational, verification, tracking, logistics, communication, and workflow infrastructure designed
                to support modern wholesale operations. <b>We do not act as a carrier, insurance company, or claims adjudicator.</b>
                Shipment protection options are provided by third-party protection partners under their own coverage terms.
              </p>
              <Link to="/legal/shipping-terms" data-testid="footer-shipping-terms-link" className="inline-flex items-center gap-1 mt-3 text-xs brand-text hover:underline">
                Read full shipping terms <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl font-black tracking-tighter">
            Start shipping in <span className="brand-text">under 90 seconds</span>.
          </h2>
          <p className="text-sm sm:text-base text-zinc-600 mt-3 sm:mt-4">Open your account, fund your wallet, and layer on Smart Services at will.</p>
          <Link to="/register" className="inline-flex mt-6 sm:mt-8 brand-bg text-white px-6 sm:px-8 py-3 rounded-sm font-medium hover:opacity-90 text-sm sm:text-base">
            Open account →
          </Link>
        </div>
      </section>

      <footer className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-6 sm:gap-8">
            <div className="col-span-2 md:col-span-4">
              <BrandLogo />
              <div className="mt-3 inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.2em] uppercase brand-bg text-white px-2 py-0.5 rounded-sm">
                <ShieldCheck size={10} /> Wholesale
              </div>
              <p className="mt-4 text-xs text-zinc-600 leading-relaxed max-w-xs">
                SellePack is the wholesale fulfillment platform of the Sellepack network. By-approval merchants receive
                negotiated rates, AML compliance monitoring, and access to Selle Check chargeback guarantees.
              </p>
              <div className="mt-4 text-[10px] text-zinc-500 font-mono-plex">compliance@sellepack.com</div>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Platforms</div>
              <ul className="space-y-1.5 text-xs">
                {PLATFORMS.map((p) => (
                  <li key={p.name}>
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`footer-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-zinc-700 hover:text-zinc-900"
                    >
                      {p.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Wholesale Programs</div>
              <ul className="space-y-1.5 text-xs">
                <li><a href="#services" className="text-zinc-700 hover:text-zinc-900">Smart Services</a></li>
                <li><a href="#click-pic" className="text-zinc-700 hover:text-zinc-900">Click Pic — 4 Programs</a></li>
                <li><a href="#payments" className="text-zinc-700 hover:text-zinc-900">Payments &amp; Guarantees</a></li>
                <li><a href="#pricing" className="text-zinc-700 hover:text-zinc-900">Pricing tiers</a></li>
                <li><Link to="/register" className="text-zinc-700 hover:text-zinc-900">Apply for wholesale</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Legal</div>
              <ul className="space-y-1.5 text-xs">
                <li><Link to="/legal/terms" data-testid="footer-legal-terms" className="text-zinc-700 hover:text-zinc-900">Wholesale Terms</Link></li>
                <li><Link to="/legal/shipping-terms" data-testid="footer-legal-shipping" className="text-zinc-700 hover:text-zinc-900">Shipping Terms</Link></li>
                <li><Link to="/legal/regulatory-disclosure" data-testid="footer-legal-regulatory" className="text-zinc-700 hover:text-zinc-900">Regulatory</Link></li>
                <li><Link to="/legal/privacy" data-testid="footer-legal-privacy" className="text-zinc-700 hover:text-zinc-900">Privacy</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-[10px] sm:text-xs text-zinc-500 font-mono-plex">
            <span>© {new Date().getFullYear()} SellePack Wholesale · Global Solutions / Protected !!</span>
            <span>Companeze and SelleSend are related companies in the Sellepack network.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ className = "", icon: Icon, title, body }) {
  return (
    <div className={`${className} bg-white border border-zinc-200 p-6 sm:p-8 rounded-md hover:border-zinc-400 transition-colors relative overflow-hidden`}>
      <Icon size={20} strokeWidth={1.5} className="brand-text" />
      <h3 className="font-display text-lg sm:text-xl font-bold mt-3 sm:mt-4 text-zinc-900">{title}</h3>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
